// Horas de los servicios (formato nuevo): la hora de inicio y fin de cada
// servicio y, en cada complemento, a qué servicio acompaña. Un solo control
// para el Resumen y para Logística > Horario: se cambia en un sitio y se ve en
// el otro, con los mismos guardados.
//
// - HorarioServicioUnDia: el control de una línea en un evento de un día
//   (Hora [inicio] a [fin], chip "Sin hora (propuesta ...)" o "Acompaña a").
//   Guarda con `onCambiar(indice, cambios)` (en la ficha, actualizarHorarioLinea:
//   la cola del autoguardado, igual que el resto de las líneas).
// - HorasServicios (por defecto): la lista de servicios del ámbito que se ve en
//   Horario.
//   · Un día: cada línea con HorarioServicioUnDia.
//   · Multievento sin jornada elegida ("Todas"): cada línea de la cabecera con
//     HorarioServicioMultievento (la misma hora para todos los días, como en el
//     Resumen).
//   · Multievento con una jornada: los servicios de ESA jornada con su hora del
//     día. Cambiarla aquí es la hora propia de ese día (`hora_servicio_manual:
//     true`, como en "Editar menús" de la jornada): PUT de la jornada con su
//     `menus_personalizados` completo (onGuardarJornada). "Volver a la del
//     resto" pone la de la cabecera y la vuelve a sincronizar; "Cambiar para
//     todos los días" manda esa hora al endpoint del servicio con alcance
//     "sincronizadas" (los días con hora propia la conservan). Los
//     complementos dicen a qué servicio acompañan (en todos los días).
//   El aviso de horas que faltan y "Proponer horas" (POST /horarios/proponer:
//   con "Todas", la cabecera y los días sin hora; con una jornada, solo ese
//   día desde su hora de inicio) van arriba.
//
// Permisos (F1, 16-oct-2026): quien no tiene el módulo de presupuestos
// (`usePuedeEditarHorarios`) ve las horas, el tipo y "Acompaña a" como texto,
// sin campos ni botones. Si el estado de las reglas no le llega (403), la
// clasificación sale de GET /presupuestos/{id}/horarios/avisos.
// Avisos (solo aviso): "Sin hora de fin" (F3) y "Fuera del horario del día"
// (F6) en ámbar.
import React, { useEffect, useRef, useState } from 'react';
import { presupuestosAPI } from '../../api/api';
import { toast } from '../../hooks/use-toast';
import usePuedeEditarHorarios from '../../hooks/usePuedeEditarHorarios';
import HorarioServicioMultievento, { guardarHorarioServicio } from './HorarioServicioMultievento';
import { esLineaVentaAparte, estadoHoraServicioJornada, lineaCabecera, lineaResuelta } from '../../utils/multieventoCabecera';
import { hoyMadrid, textoDetalleApi } from '../../utils/fechas';
import {
  CLASE_ERROR_FRANJA, errorFranja, fueraDeHorario, horarioReferenciaJornada, textoHorasServicio,
} from '../../utils/horasServicio';

const RETARDO_MS = 800;

// Inputs de hora: al menos 32 px en móvil, compactos en escritorio
export const CLASE_HORARIO = 'h-8 sm:h-6 px-1 border border-[#CBD3CF] rounded text-xs text-[#33443C] bg-white tabular-nums focus:outline-none focus:ring-1 focus:ring-duetto-primary disabled:opacity-60 disabled:cursor-not-allowed';

export const chipHorario = (texto, tono = 'gris', extra = {}) => (
  <span key={texto} className={`inline-flex items-center px-1.5 py-px rounded text-[11px] font-semibold ${{
    gris: 'bg-[#EEF1EF] text-[#4A5A53]',
    ambar: 'bg-[#FDF0DC] text-[#92400E]',
    verde: 'bg-[#E3F3EC] text-[#0F5E43]',
    violeta: 'bg-violet-50 text-violet-800',
  }[tono]}`} {...extra}>{texto}</span>
);

// Fin vacío con inicio puesto (F3): se permite, pero en ámbar
export const CLASE_SIN_FIN = 'border-[#F4D9AE] bg-[#FDF0DC]';

const textoSinHora = (f) => `Sin hora${f?.propuesta_inicio ? ` (propuesta ${f.propuesta_inicio} - ${f.propuesta_fin || '?'})` : ''}`;
const nombreLinea = (l, f) => l?.nombre_menu || l?.nombre || f?.nombre || 'Línea';

// Clasificación del servidor de la línea de la cabecera en `indice`, solo si
// corresponde a la línea que hay ahora en esa posición
export const horarioDeLinea = (estadoReglas, menus, indice) => {
  const f = estadoReglas?.horarios?.lineas?.[indice];
  const m = (menus || [])[indice];
  if (!f || !m || (f.menu_id || '') !== (m.menu_id || '')) return null;
  return f;
};

// Selector "Acompaña a" de un complemento (evento de un día)
const SelectorAcompana = ({ indice, fHorario, serviciosHorario, claseHorario, menus, onCambiar, prefijo }) => (
  <label className="inline-flex items-center gap-1 min-w-0">
    <span className="font-semibold whitespace-nowrap">Acompaña a</span>
    <select
      value={fHorario?.acompana_a_indice ?? ''}
      onChange={(e) => {
        const destino = parseInt(e.target.value, 10);
        if (Number.isNaN(destino)) return;
        // También por linea_id (manda al leer; así no se mueve al quitar líneas)
        const lid = (menus || [])[destino]?.linea_id || null;
        onCambiar(indice, { acompana_a_indice: destino, acompana_a_linea_id: lid });
      }}
      className={`${claseHorario} min-w-0 max-w-[16rem]`}
      title="Servicio al que acompaña este complemento"
      data-uso="presupuesto.linea_acompana"
      data-testid={`${prefijo}-acompana-${indice}`}
    >
      {fHorario?.acompana_a_indice == null && <option value="" disabled>Elegir servicio</option>}
      {serviciosHorario.map(sv => (
        <option key={sv.indice} value={sv.indice}>
          {sv.nombre}{sv.hora_inicio ? ` (${sv.hora_inicio})` : ''}
        </option>
      ))}
    </select>
  </label>
);

// "Acompaña a Desayuno (09:00)" en solo lectura
export const textoAcompana = (fHorario, serviciosHorario = []) => {
  const sv = (serviciosHorario || []).find(x => x.indice === fHorario?.acompana_a_indice);
  const nombre = sv?.nombre || fHorario?.acompana_a_nombre;
  if (!nombre) return 'Complemento';
  return `Acompaña a ${nombre}${sv?.hora_inicio ? ` (${sv.hora_inicio})` : ''}`;
};

// Control de horario de una línea de un evento de un día (Resumen y Horario).
// Con la pareja inválida (fin antes que inicio) no se guarda: se ve en rojo
// hasta que se corrige. `placeholderInicio` / `placeholderFin` son la hora
// del evento: también sirven para el aviso "Fuera del horario" (F6).
// `soloLectura` (por defecto, sin el módulo de presupuestos): texto.
export const HorarioServicioUnDia = ({
  indice, linea, fHorario, esComplemento = false, serviciosHorario = [], claseHorario = CLASE_HORARIO,
  guia = false, chip = chipHorario, placeholderInicio = '', placeholderFin = '', menus, onCambiar, prefijo = 'r2',
  soloLectura = null,
}) => {
  const puedeEditar = usePuedeEditarHorarios();
  const lectura = soloLectura ?? !puedeEditar;
  const [borrador, setBorrador] = useState(null);
  const ini = borrador ? borrador.ini : (linea?.hora_inicio_servicio || '');
  const fin = borrador ? borrador.fin : (linea?.hora_fin_servicio || '');
  const error = errorFranja(ini, fin);
  const esServicio = !esComplemento && (fHorario ? fHorario.clase === 'servicio' : true);
  const sinFin = esServicio && !!ini && !fin && !error;
  const fuera = esServicio && !error ? fueraDeHorario(ini, fin, placeholderInicio, placeholderFin) : null;
  const avisos = (
    <>
      {sinFin && chip('Sin hora de fin', 'ambar', { 'data-testid': `${prefijo}-sin-fin-${indice}` })}
      {fuera && chip(fuera, 'ambar', { 'data-testid': `${prefijo}-fuera-horario-${indice}` })}
    </>
  );
  if (lectura) {
    if (esComplemento) {
      return chip(textoAcompana(fHorario, serviciosHorario), 'gris', { 'data-testid': `${prefijo}-acompana-texto-${indice}` });
    }
    if (!fHorario && !ini && !fin) return null;
    return (
      <>
        <span className="inline-flex items-center gap-1" data-testid={`${prefijo}-horas-texto-${indice}`}>
          <span className="font-semibold">Hora</span>
          <span className="tabular-nums font-semibold text-[#33443C]">{textoHorasServicio(ini, fin)}</span>
        </span>
        {fHorario?.clase === 'servicio' && fHorario.sin_hora
          && chip('Sin hora', 'ambar', { 'data-testid': `${prefijo}-sin-hora-${indice}` })}
        {avisos}
      </>
    );
  }
  const cambiarHora = (campo, valor) => {
    const nuevo = { ini, fin, [campo]: valor };
    if (errorFranja(nuevo.ini, nuevo.fin)) {
      setBorrador(nuevo);
      return;
    }
    setBorrador(null);
    onCambiar(indice, { hora_inicio_servicio: nuevo.ini, hora_fin_servicio: nuevo.fin });
  };
  return (
  <>
    {!esComplemento && (
      <span className="inline-flex items-center gap-1" data-guia={guia ? 'linea-horas' : undefined}>
        <span className="font-semibold">Hora</span>
        <input
          type="time"
          value={ini}
          placeholder={placeholderInicio}
          onChange={(e) => cambiarHora('ini', e.target.value)}
          className={`${claseHorario} w-[5.5rem]`}
          title="Hora de inicio del servicio"
          aria-label="Hora de inicio del servicio"
          data-uso="presupuesto.linea_horas"
          data-testid={`${prefijo}-hora-inicio-${indice}`}
        />
        <span>a</span>
        <input
          type="time"
          value={fin}
          placeholder={placeholderFin}
          onChange={(e) => cambiarHora('fin', e.target.value)}
          className={`${claseHorario} w-[5.5rem] ${sinFin ? CLASE_SIN_FIN : ''}`}
          title="Hora de fin del servicio"
          aria-label="Hora de fin del servicio"
          data-uso="presupuesto.linea_horas"
          data-testid={`${prefijo}-hora-fin-${indice}`}
        />
        {error && (
          <span className={CLASE_ERROR_FRANJA} data-testid={`${prefijo}-hora-error-${indice}`}>
            {error}{borrador ? ': no se guarda' : ''}
          </span>
        )}
      </span>
    )}
    {!esComplemento && fHorario?.clase === 'servicio' && fHorario.sin_hora
      && chip(textoSinHora(fHorario), 'ambar', { 'data-testid': `${prefijo}-sin-hora-${indice}` })}
    {avisos}
    {esComplemento && (serviciosHorario.length > 0 ? (
      <SelectorAcompana indice={indice} fHorario={fHorario} serviciosHorario={serviciosHorario}
        claseHorario={claseHorario} menus={menus} onCambiar={onCambiar} prefijo={prefijo} />
    ) : chip('Complemento', 'gris'))}
  </>
  );
};

// Fila de la lista: nombre del servicio y su control debajo
const Fila = ({ nombre, testid, children }) => (
  <li className="py-2.5 first:pt-0 last:pb-0" data-testid={testid}>
    <p className="text-sm font-semibold text-[#16221D] truncate" title={nombre}>{nombre}</p>
    <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-[#5B6B64]">{children}</div>
  </li>
);

// ¿La jornada ya no admite cambios de hora? 'facturada' | 'celebrada' | null
// (mismo criterio que horarioServicioPorJornada)
const bloqueoJornada = (j) => {
  if (!j) return null;
  if (j.facturada || j.factura_id) return 'facturada';
  const fecha = String(j.fecha ?? '').slice(0, 10);
  return fecha && fecha < hoyMadrid() ? 'celebrada' : null;
};

// Servicios de una jornada del multievento (con su línea de la cabecera)
export const serviciosDeJornada = (presupuesto, jornada, estadoReglas) => {
  const raiz = presupuesto?.menus_personalizados || [];
  const res = [];
  (Array.isArray(jornada?.menus_personalizados) ? jornada.menus_personalizados : []).forEach((l, pos) => {
    if (!l || typeof l !== 'object' || esLineaVentaAparte(l)) return;
    const cab = lineaCabecera(l, raiz);
    if (!cab) return;
    const indiceCab = raiz.indexOf(cab);
    const f = horarioDeLinea(estadoReglas, raiz, indiceCab);
    if (!f || f.clase === 'suplemento') return;
    res.push({
      pos, linea: l, cab, indiceCab, fHorario: f,
      nombre: nombreLinea(lineaResuelta(l, raiz), f),
      estado: estadoHoraServicioJornada(l, cab),
    });
  });
  return res;
};

// Servicios de la jornada sin hora en la jornada (aunque la cabecera la tenga:
// la orden de cocina nueva y la hoja de material leen la del día)
export const serviciosSinHoraJornada = (presupuesto, jornada, estadoReglas) => serviciosDeJornada(
  presupuesto, jornada, estadoReglas,
).filter(s => s.fHorario.clase === 'servicio' && !s.estado.inicio);

// Multievento con una jornada elegida: la hora de cada servicio ESE día
const HorasJornada = ({
  presupuesto, jornada, estadoReglas, serviciosHorario, onGuardarJornada, onActualizado, onAntesDeGuardar = null,
  soloLectura = false,
}) => {
  const servicios = serviciosDeJornada(presupuesto, jornada, estadoReglas);
  const bloqueo = bloqueoJornada(jornada);
  const [refIni, refFin] = horarioReferenciaJornada(jornada, presupuesto);
  // Horas mientras se escriben, por posición de la línea en la jornada
  const [borradores, setBorradores] = useState({});
  const [guardando, setGuardando] = useState(false);
  const borradoresRef = useRef({});
  const timersRef = useRef({});
  const colaRef = useRef(Promise.resolve());
  const montadoRef = useRef(true);
  // Borrador que se está enviando por posición: al salir del campo justo
  // después del retardo no se vuelve a enviar
  const enviandoRef = useRef({});
  // Líneas de la jornada sobre las que se guarda: las de la ficha o, justo
  // después de guardar, las que devolvió el servidor
  const baseRef = useRef(null);
  const jornadaVistaRef = useRef(null);
  if (jornada !== jornadaVistaRef.current) {
    jornadaVistaRef.current = jornada;
    baseRef.current = Array.isArray(jornada?.menus_personalizados) ? jornada.menus_personalizados : [];
  }
  const serviciosRef = useRef(servicios);
  serviciosRef.current = servicios;

  // Las peticiones van de una en una, en el orden en que se hacen
  const encolar = (fn) => {
    const tarea = colaRef.current.then(async () => {
      if (montadoRef.current) setGuardando(true);
      try {
        return await fn();
      } finally {
        if (montadoRef.current) setGuardando(false);
      }
    });
    colaRef.current = tarea.catch(() => null);
    return tarea;
  };

  const guardarLinea = (pos, cambios) => encolar(async () => {
    const base = baseRef.current || [];
    if (!base[pos] || !onGuardarJornada) return null;
    const menus = base.map((l, i) => (i === pos ? { ...l, ...cambios } : l));
    try {
      const j = await onGuardarJornada(jornada.id, { menus_personalizados: menus });
      if (j && Array.isArray(j.menus_personalizados)) baseRef.current = j.menus_personalizados;
      return j;
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'No se ha guardado la hora',
        description: textoDetalleApi(error?.response?.data?.detail, error?.message || 'Error'),
      });
      return null;
    }
  });

  const guardarBorrador = (pos) => {
    clearTimeout(timersRef.current[pos]);
    delete timersRef.current[pos];
    const b = borradoresRef.current[pos];
    if (!b) return Promise.resolve(null);
    const s = serviciosRef.current.find(x => x.pos === pos);
    const quitar = () => {
      if (borradoresRef.current[pos] !== b) return;
      delete borradoresRef.current[pos];
      if (montadoRef.current) setBorradores({ ...borradoresRef.current });
    };
    if (!s || (b.ini === (s.estado.inicio || '') && b.fin === (s.estado.fin || ''))) {
      quitar();
      return Promise.resolve(null);
    }
    // Fin antes que inicio: no se guarda; el borrador se queda en rojo
    if (errorFranja(b.ini, b.fin)) return Promise.resolve(null);
    if (enviandoRef.current[pos] === b) return Promise.resolve(null);
    enviandoRef.current[pos] = b;
    // Hora propia de este día: se escriben las dos horas, como se ven
    return guardarLinea(pos, { hora_inicio_servicio: b.ini, hora_fin_servicio: b.fin, hora_servicio_manual: true })
      .finally(() => {
        if (enviandoRef.current[pos] === b) delete enviandoRef.current[pos];
        quitar();
      });
  };
  const guardarRef = useRef(guardarBorrador);
  guardarRef.current = guardarBorrador;

  // Al salir (otra jornada u otra subpestaña) se guarda lo escrito
  useEffect(() => {
    montadoRef.current = true;
    const timers = timersRef.current;
    return () => {
      montadoRef.current = false;
      Object.keys(timers).forEach((pos) => {
        clearTimeout(timers[pos]);
        guardarRef.current(Number(pos));
      });
    };
  }, []);

  const cambiarHora = (s, campo, valor) => {
    const base = borradoresRef.current[s.pos] || { ini: s.estado.inicio || '', fin: s.estado.fin || '' };
    borradoresRef.current = { ...borradoresRef.current, [s.pos]: { ...base, [campo]: valor } };
    setBorradores(borradoresRef.current);
    clearTimeout(timersRef.current[s.pos]);
    timersRef.current[s.pos] = setTimeout(() => guardarRef.current(s.pos), RETARDO_MS);
  };

  const volverAlResto = (s) => {
    clearTimeout(timersRef.current[s.pos]);
    delete timersRef.current[s.pos];
    delete borradoresRef.current[s.pos];
    setBorradores({ ...borradoresRef.current });
    guardarLinea(s.pos, {
      hora_inicio_servicio: s.cab.hora_inicio_servicio || '',
      hora_fin_servicio: s.cab.hora_fin_servicio || '',
      hora_servicio_manual: false,
    });
  };

  // La hora de este día para el mismo servicio de todos los días (los que
  // tienen hora propia la conservan): endpoint del servicio, alcance "sincronizadas"
  const cambiarParaTodos = (s) => {
    const b = borradoresRef.current[s.pos];
    const ini = b ? b.ini : (s.estado.inicio || '');
    const fin = b ? b.fin : (s.estado.fin || '');
    guardarBorrador(s.pos);
    encolar(async () => {
      const data = await guardarHorarioServicio({
        presupuestoId: presupuesto?.id, indice: s.indiceCab, linea: s.cab,
        cambios: { hora_inicio: ini, hora_fin: fin }, alcance: 'sincronizadas', antes: onAntesDeGuardar,
      });
      if (data && onActualizado) await onActualizado();
      return data;
    });
  };

  if (servicios.length === 0) {
    return <p className="text-sm text-[#5B6B64]">Esta jornada no tiene servicios con hora.</p>;
  }
  return (
    <div className="flex flex-col gap-3">
      {bloqueo && (
        <p className="text-xs text-[#5B6B64]" data-testid="l2-horas-jornada-bloqueo">
          Esta jornada ya está {bloqueo}: sus horas no se cambian.
        </p>
      )}
      <ul className="flex flex-col divide-y divide-[#EEF1EF]">
        {servicios.map((s) => {
          const esComplemento = s.fHorario.clase === 'complemento';
          if (esComplemento) {
            return (
              <Fila key={`c-${s.pos}`} nombre={s.nombre} testid={`l2-horas-servicio-${s.pos}`}>
                <HorarioServicioMultievento
                  presupuesto={presupuesto}
                  indice={s.indiceCab}
                  linea={s.cab}
                  fHorario={s.fHorario}
                  serviciosHorario={serviciosHorario}
                  claseHorario={CLASE_HORARIO}
                  chip={chipHorario}
                  onActualizado={onActualizado}
                  onAntesDeGuardar={onAntesDeGuardar}
                  soloLectura={soloLectura}
                />
                <span className="text-[#8A9892]">todos los días</span>
              </Fila>
            );
          }
          const b = borradores[s.pos];
          const ini = b ? b.ini : (s.estado.inicio || '');
          const fin = b ? b.fin : (s.estado.fin || '');
          // Mientras se escribe ya es la hora propia del día
          const propia = b ? (ini !== (s.cab.hora_inicio_servicio || '') || fin !== (s.cab.hora_fin_servicio || '')) : s.estado.propia;
          const errorHoras = errorFranja(ini, fin);
          const esServicio = s.fHorario.clase === 'servicio';
          const sinFin = esServicio && !!ini && !fin && !errorHoras;
          const fuera = esServicio && !errorHoras ? fueraDeHorario(ini, fin, refIni, refFin, 'día') : null;
          const avisosDia = (
            <>
              {sinFin && chipHorario('Sin hora de fin', 'ambar', { 'data-testid': `l2-sin-fin-jornada-${s.pos}` })}
              {fuera && chipHorario(fuera, 'ambar', { 'data-testid': `l2-fuera-horario-jornada-${s.pos}` })}
            </>
          );
          if (soloLectura) {
            return (
              <Fila key={`s-${s.pos}`} nombre={s.nombre} testid={`l2-horas-servicio-${s.pos}`}>
                <span className="inline-flex items-center gap-1" data-testid={`l2-horas-texto-jornada-${s.pos}`}>
                  <span className="font-semibold">Hora</span>
                  <span className="tabular-nums font-semibold text-[#33443C]">{textoHorasServicio(ini, fin)}</span>
                </span>
                {esServicio && !ini && chipHorario('Sin hora este día', 'ambar', { 'data-testid': `l2-sin-hora-jornada-${s.pos}` })}
                {avisosDia}
                {ini && propia && <span className="font-semibold text-[#92400E]">Hora propia de este día</span>}
              </Fila>
            );
          }
          return (
            <Fila key={`s-${s.pos}`} nombre={s.nombre} testid={`l2-horas-servicio-${s.pos}`}>
              <span className="inline-flex items-center gap-1">
                <span className="font-semibold">Hora</span>
                <input
                  type="time"
                  value={ini}
                  disabled={!!bloqueo}
                  onChange={(e) => cambiarHora(s, 'ini', e.target.value)}
                  onBlur={() => guardarRef.current(s.pos)}
                  className={`${CLASE_HORARIO} w-[5.5rem]`}
                  title="Hora de inicio del servicio este día"
                  aria-label="Hora de inicio del servicio este día"
                  data-uso="presupuesto.jornada_horas"
                  data-testid={`l2-hora-inicio-jornada-${s.pos}`}
                />
                <span>a</span>
                <input
                  type="time"
                  value={fin}
                  disabled={!!bloqueo}
                  onChange={(e) => cambiarHora(s, 'fin', e.target.value)}
                  onBlur={() => guardarRef.current(s.pos)}
                  className={`${CLASE_HORARIO} w-[5.5rem] ${sinFin ? CLASE_SIN_FIN : ''}`}
                  title="Hora de fin del servicio este día"
                  aria-label="Hora de fin del servicio este día"
                  data-uso="presupuesto.jornada_horas"
                  data-testid={`l2-hora-fin-jornada-${s.pos}`}
                />
                {errorHoras && (
                  <span className={CLASE_ERROR_FRANJA} data-testid={`l2-hora-error-jornada-${s.pos}`}>
                    {errorHoras}{b ? ': no se guarda' : ''}
                  </span>
                )}
              </span>
              {s.fHorario.clase === 'servicio' && !ini && chipHorario(
                !b && s.estado.sinHora
                  ? `Sin hora este día (el resto: ${s.estado.cabInicio || '?'} a ${s.estado.cabFin || '?'})`
                  : textoSinHora(s.fHorario),
                'ambar', { 'data-testid': `l2-sin-hora-jornada-${s.pos}` })}
              {avisosDia}
              {!b && s.estado.sinHora && !bloqueo && (
                <button
                  type="button"
                  onClick={() => volverAlResto(s)}
                  disabled={guardando}
                  className="font-semibold text-[#11704F] hover:underline disabled:opacity-50"
                  title="Poner en este día la hora del resto de días"
                  data-testid={`l2-hora-poner-resto-jornada-${s.pos}`}
                >
                  Poner la del resto
                </button>
              )}
              {ini && (
                <span className={propia ? 'font-semibold text-[#92400E]' : 'text-[#5B6B64]'}
                  data-testid={`l2-hora-estado-jornada-${s.pos}`}>
                  {propia ? 'Hora propia de este día' : 'Igual que el resto de días'}
                </span>
              )}
              {propia && !bloqueo && (
                <>
                  <button
                    type="button"
                    onClick={() => volverAlResto(s)}
                    disabled={guardando}
                    className="font-semibold text-[#11704F] hover:underline disabled:opacity-50"
                    title={`Poner la hora del resto de días${s.cab.hora_inicio_servicio ? ` (${s.cab.hora_inicio_servicio}${s.cab.hora_fin_servicio ? ` a ${s.cab.hora_fin_servicio}` : ''})` : ''}`}
                    data-testid={`l2-hora-resto-jornada-${s.pos}`}
                  >
                    Volver a la del resto
                  </button>
                  <button
                    type="button"
                    onClick={() => cambiarParaTodos(s)}
                    disabled={guardando || !ini || !!errorHoras}
                    className="font-semibold text-[#11704F] hover:underline disabled:opacity-50"
                    title="Poner esta hora en este servicio en todos los días (los que tienen hora propia la conservan)"
                    data-testid={`l2-hora-todos-jornada-${s.pos}`}
                  >
                    Cambiar para todos los días
                  </button>
                </>
              )}
            </Fila>
          );
        })}
      </ul>
      {guardando && <p className="text-[11px] text-[#8A9892]" data-testid="l2-horas-jornada-guardando">Guardando...</p>}
    </div>
  );
};

const HorasServicios = ({
  presupuesto, estadoReglas, jornada = null,
  // Un día: (indice, cambios) => guarda la línea (actualizarHorarioLinea)
  onCambiarLinea = null,
  // Multievento: (jornadaId, cambios) => Promise<jornada> y recarga de la ficha
  onGuardarJornada = null, onActualizado = null,
  // "Proponer horas" (POST /horarios/proponer) y si está en curso
  onProponerHoras = null, proponiendo = false,
  // Antes del endpoint del horario del multievento: guardar lo pendiente de
  // la ficha (autoguardado.guardarYa)
  onAntesDeGuardar = null,
}) => {
  const p = presupuesto || {};
  const puedeEditar = usePuedeEditarHorarios();
  const soloLectura = !puedeEditar;
  // Sin el módulo de presupuestos el estado de las reglas no llega (403): la
  // clasificación de las líneas sale del endpoint de avisos (solo lectura)
  const [horariosLectura, setHorariosLectura] = useState(null);
  const sinEstado = !estadoReglas?.horarios;
  useEffect(() => {
    if (!soloLectura || !sinEstado || !p.id) return undefined;
    let cancelado = false;
    presupuestosAPI.avisosHorarios(p.id)
      .then(({ data }) => { if (!cancelado) setHorariosLectura(data?.horarios || { aplica: false, lineas: [] }); })
      .catch(() => { if (!cancelado) setHorariosLectura({ aplica: false, lineas: [] }); });
    return () => { cancelado = true; };
  }, [soloLectura, sinEstado, p.id, p.rev]);
  const estado = estadoReglas?.horarios ? estadoReglas : (horariosLectura ? { horarios: horariosLectura } : null);
  const h = estado?.horarios;
  const menus = p.menus_personalizados || [];
  const multi = !!p.es_multi_evento && Array.isArray(p.jornadas) && p.jornadas.length > 0;
  const serviciosHorario = (h?.lineas || []).filter(x => x.clase === 'servicio');

  if (!h) return <p className="text-sm text-[#5B6B64]">Cargando las horas de los servicios...</p>;

  // Servicios sin hora en lo que se ve: la jornada elegida o la cabecera
  const sinHora = jornada
    ? serviciosSinHoraJornada(p, jornada, estado).length
    : (h.aplica ? Number(h.sin_hora) || 0 : 0);
  const proponer = onProponerHoras && sinHora > 0 && !soloLectura ? (
    <button
      type="button"
      onClick={() => onProponerHoras(jornada ? jornada.id : null)}
      disabled={proponiendo}
      className="self-start h-[30px] px-3 rounded-lg border text-xs font-bold whitespace-nowrap bg-duetto-primary border-duetto-primary text-white hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
      title={jornada
        ? 'Rellena la hora de los servicios de este día que no la tienen, desde la hora de inicio del día (no cambia las ya puestas ni los demás días)'
        : (multi
          ? 'Rellena la hora de los servicios que no la tienen y la lleva a los días sin hora (no cambia las ya puestas ni las horas propias)'
          : 'Rellena la hora de los servicios que no la tienen (no cambia las ya puestas)')}
      data-testid="l2-proponer-horas"
    >
      {proponiendo ? 'Proponiendo...' : 'Proponer horas'}
    </button>
  ) : null;

  let lista;
  if (jornada) {
    lista = (
      <HorasJornada
        key={jornada.id}
        presupuesto={p}
        jornada={jornada}
        estadoReglas={estado}
        serviciosHorario={serviciosHorario}
        onGuardarJornada={onGuardarJornada}
        onActualizado={onActualizado}
        onAntesDeGuardar={onAntesDeGuardar}
        soloLectura={soloLectura}
      />
    );
  } else {
    const filas = menus.map((m, idx) => {
      if (!m || esLineaVentaAparte(m)) return null;
      const f = horarioDeLinea(estado, menus, idx);
      if (!f || f.clase === 'suplemento') return null;
      const nombre = nombreLinea(m, f);
      return (
        <Fila key={m.linea_id || `${m.menu_id || nombre}-${idx}`} nombre={nombre} testid={`l2-horas-servicio-${idx}`}>
          {multi ? (
            <HorarioServicioMultievento
              presupuesto={p}
              indice={idx}
              linea={m}
              fHorario={f}
              serviciosHorario={serviciosHorario}
              claseHorario={CLASE_HORARIO}
              chip={chipHorario}
              onActualizado={onActualizado}
              onAntesDeGuardar={onAntesDeGuardar}
              soloLectura={soloLectura}
            />
          ) : (
            <HorarioServicioUnDia
              indice={idx}
              linea={m}
              fHorario={f}
              esComplemento={f.clase === 'complemento'}
              serviciosHorario={serviciosHorario}
              placeholderInicio={p.hora_inicio || ''}
              placeholderFin={p.hora_fin || ''}
              menus={menus}
              onCambiar={onCambiarLinea}
              prefijo="l2"
              soloLectura={soloLectura}
            />
          )}
        </Fila>
      );
    }).filter(Boolean);
    lista = filas.length > 0
      ? <ul className="flex flex-col divide-y divide-[#EEF1EF]">{filas}</ul>
      : <p className="text-sm text-[#5B6B64]">No hay servicios con hora: se añaden en el Resumen.</p>;
  }

  return (
    <div className="flex flex-col gap-3" data-guia="horario-servicios">
      <p className="text-xs text-[#5B6B64]">
        {soloLectura
          ? 'Solo lectura: las horas las cambia quien gestiona el presupuesto.'
          : (jornada
            ? 'La hora de cada servicio este día. Si la cambias aquí, es la hora propia de este día; el resto de días no cambia.'
            : 'Las mismas horas que en el Resumen: se cambian en un sitio y se ven en el otro. Los complementos van con el servicio al que acompañan.')}
      </p>
      {proponer}
      {lista}
    </div>
  );
};

export default HorasServicios;
